import clsx from 'clsx';
import React from 'react';

import {
    type ImageColumnDefinition,
    type TableDefinitionImageRecord,
    table,
} from '@gitbook/doc-core';
import { createPalette, usePaletteHandle } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { AtomButton, ImageIcon, LoadingIndicator } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { useContentReference } from '../../../ContentReferences';
import { FileIcon } from '../../../Files';
import { useInsertFiles } from '../../../InsertFilesDialog';
import { ContentEditorContext } from '../../contexts';
import { InsertValueButton } from './buttons';
import type { RecordValueRenderProps, TableDefinitionRenderSpec } from './types';

type ImageContentRef = TableDefinitionImageRecord;

export const imageDefinitionSpec: TableDefinitionRenderSpec<
    ImageContentRef,
    ImageColumnDefinition
> = {
    title: 'Image',
    description: 'Add an image which can be viewed, used as a cover image, or downloaded.',
    icon: Icon.Image,
    ValueComponent: RecordImageValue,
    onKeyDown: (key, _value, setValue) => {
        switch (key) {
            case 'backspace':
                setValue(null);
                break;
        }
    },
};

/**
 * Cell content for an Image column.
 */
function RecordImageValue(
    props: RecordValueRenderProps<ImageContentRef | null, ImageColumnDefinition>
): React.ReactElement {
    const { value, selected, readOnly, displayMode, onValueChange, onKeyDown } = props;
    const {
        blocks: { withImages },
    } = React.useContext(ContentEditorContext);

    const addButtonRef = React.useRef<HTMLButtonElement>(null);
    const insertFiles = useInsertFiles();

    // If the current cell is `selected`, focus the + button
    React.useEffect(() => {
        return selected ? addButtonRef.current?.focus() : addButtonRef.current?.blur();
    }, [selected]);

    /** User pressed to select an image */
    const onPressAdd = withImages
        ? async () => {
              const [file] = await insertFiles.prompt({
                  title: 'Select image',
                  accept: 'images',
                  acceptURL: true,
              });

              if (!file) {
                  return;
              }

              onValueChange(file);
              // Focus the + button after closing the image dialog
              addButtonRef.current?.focus();
          }
        : null;

    /** Remove an image from the listing */
    const onRemove = withImages
        ? () => {
              onValueChange(null);
          }
        : null;

    return (
        <div className="relative flex flex-1 flex-wrap gap-1">
            <React.Suspense fallback={<LoadingIndicator />}>
                {value ? (
                    <FileAttachmentItem
                        value={value}
                        editable={
                            readOnly || !onRemove ? undefined : { onRemove: () => onRemove() }
                        }
                    />
                ) : null}
            </React.Suspense>
            {(displayMode === 'modal' || selected) && !readOnly && onPressAdd ? (
                <InsertValueButton
                    buttonRef={addButtonRef}
                    leadingIcon={value ? Icon.Pencil : Icon.Plus}
                    label="Add image"
                    tooltip={{
                        label: 'Add image',
                        shortcut: 'space',
                    }}
                    onKeyDown={onKeyDown}
                    onPress={onPressAdd}
                    active={insertFiles.isOpen()}
                />
            ) : null}
        </div>
    );
}

export function getImageGroupCommands(params: {
    editable: {
        onRemove: () => void;
    };
    href: string | undefined;
}) {
    const { editable, href } = params;

    return {
        groups: [
            {
                key: 'image',
                commands: [
                    {
                        key: 'remove',
                        title: 'Remove',
                        icon: Icon.Trashcan,
                        trigger: () => {
                            editable.onRemove();
                        },
                    },
                    href
                        ? {
                              key: 'download',
                              title: 'Download',
                              icon: Icon.Download,
                              trigger: () => {
                                  window.open(href, '_blank');
                              },
                          }
                        : null,
                ].filter(filterOutNullable),
            },
        ],
    };
}

/** Item for an image in the table */
function FileAttachmentItem(props: {
    value: ImageContentRef;
    editable?: {
        onRemove: () => void;
    };
}): React.ReactElement {
    const { value, editable } = props;
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(buttonRef);
    const isExpandedImageRecordFormat = table.cards.isExpandedImageRecordFormat(value);
    const resolved = useContentReference(isExpandedImageRecordFormat ? value.ref : value);
    const file = resolved.resolution?.extra?.file;

    const onPress = () => {
        if (!editable) {
            if (resolved.href) {
                window.open(resolved.href, '_blank');
            }
        } else {
            paletteHandle.open(
                createPalette({
                    ...getImageGroupCommands({ editable, href: resolved.href }),
                })
            );
        }
    };

    return (
        <AtomButton
            ref={buttonRef}
            onClick={onPress}
            type="button"
            className={clsx('flex flex-grow', !editable && 'cursor-pointer hover:text-primary')}
        >
            <div className="flex flex-1 items-center gap-2">
                {file ? (
                    <FileIcon file={file} size={24} className="shrink-0" />
                ) : (
                    <ImageIcon src={resolved.href} size={24} className="shrink-0" />
                )}
                <span className="truncate">{resolved.title || 'Deleted'}</span>
            </div>
        </AtomButton>
    );
}
